import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

export default function DPA() {
  return (
    <LegalLayout title="Data Processing Agreement" eyebrow="Legal · DPA" lastUpdated="1 March 2026" version="1.0">
      <p>
        This Data Processing Agreement ("<strong>DPA</strong>") supplements and forms part of the principal services
        agreement between <strong>Connexis Group Ltd</strong>, company number <strong>17214446</strong> ("<strong>Connexis</strong>")
        and its Buyer, Publisher or Affiliate counterparty ("<strong>Customer</strong>"). It governs the processing
        of personal data by Connexis on behalf of, or jointly with, the Customer.
      </p>

      <h2>1. Definitions</h2>
      <p>
        Terms such as "Personal Data", "Processing", "Data Subject", "Controller" and "Processor" have the meanings
        given to them in the UK GDPR (and where applicable the EU GDPR).
      </p>

      <h2>2. Roles of the parties</h2>
      <ul>
        <li>
          For <strong>account data</strong> (the Customer's users, billing contacts, support communications), Connexis
          is the <strong>Controller</strong>.
        </li>
        <li>
          For <strong>Lead data</strong> routed from a Publisher to a Buyer, Connexis and the relevant Buyer are{" "}
          <strong>Joint Controllers</strong> from the point of delivery, each acting under their respective lawful
          basis.
        </li>
        <li>
          For data <strong>processed on the Customer's behalf</strong> at the Customer's instruction (e.g. CRM
          delivery, postback tracking), Connexis acts as a <strong>Processor</strong>.
        </li>
      </ul>

      <h2>3. Subject matter, duration, nature &amp; purpose</h2>
      <ul>
        <li><strong>Subject matter</strong>: provision of the Connexis routing and AI infrastructure.</li>
        <li><strong>Duration</strong>: for the duration of the principal agreement.</li>
        <li><strong>Nature &amp; purpose</strong>: matching consumer enquiries to authorised buyers, delivery, billing, payouts, compliance, audit.</li>
        <li><strong>Categories of Data Subjects</strong>: end consumers who submitted an enquiry; Customer's authorised users.</li>
        <li><strong>Categories of Personal Data</strong>: identifiers (name, email, phone), enquiry context, consent metadata, technical metadata (IP, user-agent, timestamp).</li>
      </ul>

      <h2>4. Connexis obligations</h2>
      <p>Connexis shall:</p>
      <ul>
        <li>Process Personal Data only on documented instructions from the Customer (where acting as Processor).</li>
        <li>Ensure personnel processing Personal Data are bound by appropriate confidentiality obligations.</li>
        <li>Implement appropriate technical and organisational measures (see Annex 1).</li>
        <li>Engage sub-processors only with general authorisation (see Annex 2) and provide 14 days notice of changes.</li>
        <li>Assist the Customer with data-subject-rights requests and DPIAs where reasonable.</li>
        <li>Notify the Customer of any Personal Data Breach without undue delay (within 72 hours of discovery).</li>
        <li>On termination, delete or return Personal Data, subject to legal retention obligations.</li>
        <li>Make available all information necessary to demonstrate compliance, and submit to audits on reasonable notice.</li>
      </ul>

      <h2>5. International transfers</h2>
      <p>
        Personal Data may be transferred outside the UK to sub-processors listed in Annex 2. Such transfers are
        carried out under (i) UK adequacy regulations, (ii) the UK International Data Transfer Agreement, or (iii)
        the UK Addendum to the EU Standard Contractual Clauses, as appropriate.
      </p>

      <h2>6. Security incident response</h2>
      <ul>
        <li>24/7 monitoring with alerting on anomalous events.</li>
        <li>Incident notification email: <a href="mailto:legal@connex.is">legal@connex.is</a>.</li>
        <li>Connexis maintains a written incident response plan, tested at least annually.</li>
      </ul>

      <h2>7. Liability</h2>
      <p>
        Each party's liability under this DPA is subject to the liability provisions of the principal agreement.
      </p>

      <h2>Annex 1 — Technical &amp; organisational measures</h2>
      <ul>
        <li>Encryption in transit (TLS 1.3) and at rest (AES-256).</li>
        <li>Role-based access control with least-privilege defaults; admin actions audited.</li>
        <li>Two-factor authentication available on every account; required on admin accounts.</li>
        <li>Daily automated backups, encrypted, retained 30 days.</li>
        <li>Continuous vulnerability scanning; quarterly penetration testing.</li>
        <li>Secrets managed via environment isolation; no secrets in source control.</li>
        <li>Network: Cloudflare WAF + DDoS, dedicated VPC, no public database endpoints.</li>
        <li>Personnel: background checks, mandatory security awareness training, formal off-boarding.</li>
      </ul>

      <h2>Annex 2 — Approved sub-processors</h2>
      <ul>
        <li><strong>MongoDB Atlas</strong> (database) — EU-West (Ireland).</li>
        <li><strong>Stripe Payments UK Ltd</strong> — UK / EU.</li>
        <li><strong>Anthropic, OpenAI, Google</strong> (AI inference) — US (UK-IDTA + zero-retention agreements).</li>
        <li><strong>Cloudflare</strong> (CDN/WAF) — global edge.</li>
      </ul>
      <p className="!text-[13px] !text-slate-500">
        Connexis may engage additional operational sub-processors (e.g. transactional email and SMS providers) to
        deliver platform notifications. A current full list, including those operational vendors, is available to
        Customers on request to <a href="mailto:legal@connex.is">legal@connex.is</a>.
      </p>

      <p className="!mt-12 !text-[13px] !text-slate-500">
        This DPA is signed on behalf of Connexis Group Ltd by its appointed director, and is deemed accepted by the
        Customer when they accept the principal Terms of Service applicable to their account role.
      </p>
    </LegalLayout>
  );
}
